import { z } from 'zod';

import { GetPlanningQueryResult } from '@/api/generated';
import {
  isBeforeCutoff,
  isDepartureAfterArrival,
  QUAY_BOLLARDS,
} from '@/components/blocks/berth-plan/berth-plan.utils';
import i18n from '@/i18n/config';

export type ShipVisit = NonNullable<
  GetPlanningQueryResult['ship_visits']
>[number];

export type CellValue = string | number | null;

const bollardError = {
  error: i18n.t('shipVisits.form.bollardRange', { max: QUAY_BOLLARDS }),
};

const bollardSchema = z
  .number(bollardError)
  .int(bollardError)
  .min(1, bollardError)
  .max(QUAY_BOLLARDS, bollardError);

const departureAfterArrivalError = {
  message: i18n.t('shipVisits.form.departureAfterArrival'),
  path: ['departure_time'],
};

const arrivalBeforeCutoffError = {
  message: i18n.t('scenarios.create.customizePlanning.arrivalBeforeCutoff'),
  path: ['arrival_time'],
};

export const createShipVisitSchema = z
  .object({
    id: z.string(),
    arrival_time: z.string(),
    departure_time: z.string().min(1),
    ship_id: z.string(),
    shipping_line_id: z.string(),
    bollard_fore: bollardSchema,
    bollard_aft: bollardSchema,
    n_moves_load: z.number().int().min(0),
    n_moves_discharge: z.number().int().min(0),
  })
  .refine(
    (visit) =>
      isDepartureAfterArrival(visit.arrival_time, visit.departure_time),
    departureAfterArrivalError
  );

/** Adds the rule that a new visit can't arrive before the Planning Cut-off. */
export const getCreateShipVisitSchema = (cutoffMs?: number) =>
  createShipVisitSchema.refine(
    (visit) => !isBeforeCutoff(visit.arrival_time, cutoffMs),
    arrivalBeforeCutoffError
  );

export type CreateShipVisitSchema = z.infer<typeof createShipVisitSchema>;

/**
 * Editing an existing visit from the Berth Plan. Same rules as creating one,
 * but move counts may stay empty: `null` means unknown, not zero. Bollards
 * are only edited (and required) for a visit not yet on the plan; placed
 * visits move by dragging.
 */
export const getEditShipVisitSchema = (
  cutoffMs?: number,
  canEditBollards = false
) =>
  z
    .object({
      arrival_time: z.string().min(1),
      departure_time: z.string().min(1),
      bollard_fore: bollardSchema.optional(),
      bollard_aft: bollardSchema.optional(),
      n_moves_load: z.number().int().min(0).nullish(),
      n_moves_discharge: z.number().int().min(0).nullish(),
    })
    .refine(
      (visit) =>
        isDepartureAfterArrival(visit.arrival_time, visit.departure_time),
      departureAfterArrivalError
    )
    .refine(
      (visit) => !isBeforeCutoff(visit.arrival_time, cutoffMs),
      arrivalBeforeCutoffError
    )
    .refine((visit) => !canEditBollards || visit.bollard_fore !== undefined, {
      ...bollardError,
      path: ['bollard_fore'],
    })
    .refine((visit) => !canEditBollards || visit.bollard_aft !== undefined, {
      ...bollardError,
      path: ['bollard_aft'],
    });

export type EditShipVisitSchema = z.infer<
  ReturnType<typeof getEditShipVisitSchema>
>;

export const SIMULATION_DURATION_DAYS_MIN = 3;
export const SIMULATION_DURATION_DAYS_MAX = 30;

export const EXPLICIT_TRANSFER_CONTAINERS_MAX = 5000;

const explicitTransshipmentTransferSchema = z
  .object({
    sourceShipVisitId: z.string().min(1, {
      message: i18n.t('scenarios.create.configure.transfers.required'),
    }),
    targetShipVisitId: z.string().min(1, {
      message: i18n.t('scenarios.create.configure.transfers.required'),
    }),
    nContainers: z.number().int().min(1).max(EXPLICIT_TRANSFER_CONTAINERS_MAX),
  })
  .refine((row) => row.sourceShipVisitId !== row.targetShipVisitId, {
    message: i18n.t('scenarios.create.configure.transfers.sameVisit'),
    path: ['targetShipVisitId'],
  });

const explicitTransshipmentTransfersSchema = z
  .array(explicitTransshipmentTransferSchema)
  .superRefine((rows, ctx) => {
    const seen = new Set<string>();
    rows.forEach((row, index) => {
      const pair = `${row.sourceShipVisitId}->${row.targetShipVisitId}`;
      if (seen.has(pair)) {
        ctx.addIssue({
          code: 'custom',
          message: i18n.t('scenarios.create.configure.transfers.duplicate'),
          path: [index, 'targetShipVisitId'],
        });
      }
      seen.add(pair);
    });
  });

export const createScenarioSchema = z
  .object({
    yardId: z.string(),
    planningId: z.string(),
    simulationRuns: z.number().int().min(1).max(100),
    startTime: z.string().min(1, {
      message: i18n.t('scenarios.create.configure.startTimeRequired'),
    }),
    durationDays: z
      .number()
      .int()
      .min(SIMULATION_DURATION_DAYS_MIN)
      .max(SIMULATION_DURATION_DAYS_MAX),
    teuCapacity: z.number().int().gt(0).optional(),
    reeferCapacity: z.number().int().gt(0).optional(),
    explicitTransshipmentTransfers: explicitTransshipmentTransfersSchema,
  })
  .strict()
  .superRefine((value, ctx) => {
    const hasTeu = value.teuCapacity !== undefined;
    const hasReefer = value.reeferCapacity !== undefined;
    if (hasTeu === hasReefer) return;

    ctx.addIssue({
      code: 'custom',
      message: i18n.t('scenarios.create.configure.capacityPairRequired'),
      path: [hasTeu ? 'reeferCapacity' : 'teuCapacity'],
    });
  });

export type CreateScenarioSchema = z.infer<typeof createScenarioSchema>;
